import { useCallback } from 'react';

import { useActorRef, useSelector } from '@xstate/react';

import { useTelemetry } from '@order/Telemetry';

import { useClaimReceiptActor, useFetchReceiptActor } from './actors';
import { receiptMachine } from './receipt.machine';

import type { ReceiptMachineImplementations } from './receipt.machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useReceiptMachine = (
  receiptId: string,
  implementations?: ReceiptMachineImplementations,
) => {
  const { track } = useTelemetry();

  // ─── Actors ──────────────────────────────────────────────────────────

  const { fetchReceipt } = useFetchReceiptActor();
  const { claimReceipt } = useClaimReceiptActor();

  // ─── Actions ─────────────────────────────────────────────────────────

  const onSuccessfulClaim = useCallback<NonNullable<Actions['onSuccessfulClaim']>>(() => {
    track('receipt-claim.claim-successful');
  }, [track]);

  const onReceiptError = useCallback<NonNullable<Actions['onReceiptError']>>(
    (_args, params) => {
      track('receipt.error', { userError: params.userError });
    },
    [track],
  );

  const onClaimError = useCallback<NonNullable<Actions['onClaimError']>>(
    (_args, params) => {
      track('receipt-claim.claim-error', { userError: params.userError });
    },
    [track],
  );

  // ─── Machine ─────────────────────────────────────────────────────────

  const actorRef = useActorRef(
    receiptMachine.provide({
      actors: {
        fetchReceipt,
        claimReceipt,
        ...implementations?.actors,
      },
      actions: {
        onSuccessfulClaim,
        onReceiptError,
        onClaimError,
        ...implementations?.actions,
      },
      guards: {
        ...implementations?.guards,
      },
    }),
    { input: { receiptId } },
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isFetchingReceipt = useSelector(actorRef, (snapshot) => snapshot.matches('fetching-receipt')); // prettier-ignore
  const isClaiming = useSelector(actorRef, (snapshot) => snapshot.matches('claiming'));
  const isClaimed = useSelector(actorRef, (snapshot) => snapshot.matches('claimed'));
  const isViewing = useSelector(actorRef, (snapshot) => snapshot.matches('viewing'));
  const isViewingClaimedReceipt = useSelector(actorRef, (snapshot) => snapshot.matches({ viewing: 'claimed' })); // prettier-ignore

  const isExpiredError = useSelector(actorRef, (snapshot) => snapshot.matches({ error: 'expired' })); // prettier-ignore
  const isTimeRestrictionError = useSelector(actorRef, (snapshot) => snapshot.matches({ error: 'time-restriction' })); // prettier-ignore
  const isReceiptAlreadyClaimedError = useSelector(actorRef, (snapshot) => snapshot.matches({ error: 'already-claimed' })); // prettier-ignore
  const isTransactionNotFoundError = useSelector(actorRef, (snapshot) => snapshot.matches({ error: 'not-found' })); // prettier-ignore
  const isUnknownError = useSelector(actorRef, (snapshot) => snapshot.matches({ error: 'unknown-error' })); // prettier-ignore

  const hasError = useSelector(actorRef, (snapshot) => snapshot.matches('error'));

  // ─── Context ─────────────────────────────────────────────────────────

  const order = useSelector(actorRef, (snapshot) => snapshot.context.order);
  const error = useSelector(actorRef, (snapshot) => snapshot.context.error);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isFetchingReceipt,
    isClaiming,
    isClaimed,
    isViewing,
    isViewingClaimedReceipt,
    isExpiredError,
    isTimeRestrictionError,
    isReceiptAlreadyClaimedError,
    isTransactionNotFoundError,
    isUnknownError,
    hasError,
    order,
    error,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Actions = NonNullable<ReceiptMachineImplementations['actions']>;
